import { Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';

import { LANGUAGES, Language } from '@core/constants/language.constants';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private supportedLangs: Language[] = Object.values(LANGUAGES).filter(
    (lang): lang is Language => lang !== LANGUAGES.ZZ,
  );

  private readonly _currentLanguage = signal<Language>(LANGUAGES.EN);
  readonly currentLanguage = this._currentLanguage.asReadonly();

  constructor(
    private translateService: TranslateService,
    private appTitle: Title,
  ) {
    if (environment.development) {
      this.supportedLangs.push(LANGUAGES.ZZ);
    }
  }

  setMemberLanguage(memberLang: string, companyDefaultLang: string): void {
    if (this.isSupported(memberLang)) {
      this._currentLanguage.set(memberLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.isSupported(browserLang)) {
        this._currentLanguage.set(browserLang);
      } else if (companyDefaultLang && this.isSupported(companyDefaultLang)) {
        this._currentLanguage.set(companyDefaultLang);
      }
    }

    this.setLanguage();
  }

  setLanguage(): void {
    this.translateService.addLangs(this.supportedLangs);
    this.translateService.setDefaultLang(
      environment.development ? LANGUAGES.ZZ : LANGUAGES.EN,
    );

    this.translateService
      .use(this._currentLanguage())
      .pipe(take(1))
      .subscribe(() =>
        this.appTitle.setTitle(this.translateService.instant('app.title')),
      );

    window.WEBSPELLCHECKER_CONFIG.localization = this._currentLanguage();
  }

  setLanguageFromCookieOrBrowser(cookieLang: string): void {
    if (this.isSupported(cookieLang)) {
      this._currentLanguage.set(cookieLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.isSupported(browserLang)) {
        this._currentLanguage.set(browserLang);
      }
    }
    this.setLanguage();
  }

  getSupportedLanguages(): Language[] {
    return this.supportedLangs;
  }

  getDefaultLanguage(): Language {
    return LANGUAGES.EN;
  }

  private isSupported(lang: string): lang is Language {
    return (this.supportedLangs as string[]).includes(lang);
  }
}
